import { Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { RTLDivDirectiveDirective } from '../../directives/rtldiv-directive.directive';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatBottomSheet, MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { ResponsiveService } from '../../services/responsive.service';
import { TranslatorService } from '../../services/translator_service';
import { FilterBarSheetComponent } from './filter-bar-sheet/filter-bar-sheet.component';

@Component({
  selector: 'app-filter-bar',
  standalone: true,
  imports: [TranslateModule, RTLDivDirectiveDirective, FormsModule, ReactiveFormsModule, MatSelectModule, MatBottomSheetModule],
  templateUrl: './filter-bar.component.html',
  styleUrl: './filter-bar.component.scss'
})
export class FilterBarComponent {
  currentLang;
	translationsObj;
  isMobile: Boolean;
  ElForm: FormGroup;
  
  @ViewChild('neighborhoodSelect',{static:false}) neighborhoodSelect: MatSelect;
  neighborhood_Title:string = '';
  neighborhoodsList:any[] = [];
  displayNeighborhoodsList:any[] = [];

  @ViewChild('sitesSelect',{static:false}) sitesSelect: MatSelect;
  sites_Title:string = '';
  sitesList:any[] = [];
  displaySitesList:any[] = [];

  @ViewChild('typesSelect',{static:false}) typesSelect: MatSelect;
  types_Title:string = '';
  typesList:any[] = [];
  displayTypesList:any[] = [];

  @ViewChild('collectionModeSelect',{static:false}) collectionModeSelect: MatSelect;
  collectionMode_Title:string = '';
  collectionModeList:any[] = [];
  displayCollectionModeList:any[] = [];

  @ViewChild('eventCauseSelect',{static:false}) eventCauseSelect: MatSelect;
  eventCause_Title:string = '';
  eventCauseList:any[] = [];
  displayEventCauseList:any[] = [];

  @ViewChild('ticketTypeSelect',{static:false}) ticketTypeSelect: MatSelect;
  ticketType_Title:string = '';
  ticketTypeList:any[] = [];
  displayTicketTypeList:any[] = [];

  @ViewChild('statusSelect',{static:false}) statusSelect: MatSelect;
  status_Title:string = '';
  statusList:any[] = [];
  displayStatusList:any[] = [];

  @Input() arrayToFilter;
  @Input() page;
  @Output() filterStatus = new EventEmitter<any>();  

  bottomSheet = inject(MatBottomSheet);
  private rawDataCache: any[] = [];

  constructor(private translator: TranslatorService, private fb: FormBuilder, private responsiveService: ResponsiveService) {
    this.translator.currentLangEmitter$
		.subscribe(async value=>{						
		  this.translationsObj = await this.translator.getTranslation(value).toPromise()			 
		  this.currentLang = value;	
      this.neighborhood_Title = this.translationsObj.LIST_COLUMNS.NEIGHBORHOOD;
      this.sites_Title = this.translationsObj.NEW_WORKPLAN.SITES;
      this.types_Title = this.translationsObj.LIST_COLUMNS.BIN_TYPE;
      this.collectionMode_Title = this.translationsObj.MANAGE_BIN.INCLUDE_IN_WORKPLAN;
      this.eventCause_Title = this.translationsObj.TICKET_MANAGEMENT.EVENT_CAUSE;
      this.ticketType_Title = this.translationsObj.TICKET_MANAGEMENT.EVENT_TYPE;
      this.status_Title = this.translationsObj.TICKET_MANAGEMENT.STATUS;

      // Subscribe to arrayToFilter only after translations are loaded
      this.arrayToFilter?.subscribe(data => {
        const formValues = this.ElForm?.getRawValue();
        const hasActiveFilters = formValues &&
          Object.values(formValues).some((v: any) => Array.isArray(v) && v.length > 0);
        if (!hasActiveFilters) {
          this.rawDataCache = data;
        }
        /*get the neighborhoods from the data*/
        const neighborhoodArr = data.map(item => item.Bin_location?.bin_neighbourhood);
        //remove null,empty strings and duplicates
        this.neighborhoodsList = [...new Set(neighborhoodArr.filter(item => item && item.trim() !== ""))];
        //get the number of bins in each neighborhood
        this.neighborhoodsList = this.neighborhoodsList.map(neighborhood => ({
          neighborhood : neighborhood,
          num_of_bins_in_nbrh: data.filter(bin => bin.Bin_location.bin_neighbourhood == neighborhood).length,
          selected: false
        }));
        this.displayNeighborhoodsList = this.neighborhoodsList;

        /*get the sites from the data*/
        const sitesArr = data.map(item => item.site_name);
        this.sitesList = [...new Set(sitesArr.filter(item => item && item.trim() !== ""))];
        this.sitesList = this.sitesList.map(site => ({
          site : site,
          num_of_bins_in_site: data.filter(bin => bin.site_name == site).length,
          selected: false
        }));
        this.displaySitesList = this.sitesList;

        /*get the types from the data*/
        const typesArr = data.map(item => item.bin_type_name);
        this.typesList = [...new Set(typesArr.filter(item => item && item.trim() !== ""))];
        this.typesList = this.typesList.map(type => ({
          type : type,
          num_of_bins_in_type: data.filter(bin => bin.bin_type_name == type).length,
          selected: false
        }));
        this.displayTypesList = this.typesList;

        /*get the collection modes from the data*/
        const collectionModesArr = data.map(item => item.Bin_Restrictions?.collection_mode);
        this.collectionModeList = [...new Set(collectionModesArr.filter(item => item !== null && item?.toString().trim() !== ""))];
        this.collectionModeList = this.collectionModeList.map(collectionMode => ({
          id: collectionMode,
          name: collectionMode == 2 ? this.translationsObj.TICKET_MANAGEMENT.INCLUDE_IN_WP : this.translationsObj.MANAGE_BIN.NOT_INCLUDE_IN_WORKPLAN,
          num_of_bins_in_collection_mode: data.filter(bin => bin.Bin_Restrictions?.collection_mode == collectionMode).length,
          selected: false
        }));
        this.displayCollectionModeList = this.collectionModeList;

        /*get the event causes from the data*/
        const eventCausesArr = data
          .filter(item => item.eventCauseEn && item.eventCauseEn.trim() !== "")
          .map(item => item.eventCauseEn);
        const uniqueEventCausesEn = [...new Set(eventCausesArr)];
        this.eventCauseList = uniqueEventCausesEn.map(eventCauseEn => {
          const matchingItem = data.find(bin => bin.eventCauseEn == eventCauseEn);
          return {
            eventCauseEn: eventCauseEn,
            eventCauseHe: matchingItem ? matchingItem.eventCauseHe : '',
            num_of_bins_in_event_cause: data.filter(bin => bin.eventCauseEn == eventCauseEn).length,
            selected: false
          };
        });
        this.displayEventCauseList = this.eventCauseList;

        /*get the ticket types from the data*/
        const ticketTypesArr = data
          .filter(item => item.ticket_type && item.ticket_type.toString().trim() !== "")
          .map(item => item.ticket_type);
        const uniqueTicketTypes = [...new Set(ticketTypesArr)];
        this.ticketTypeList = uniqueTicketTypes.map(ticketType => {
          const matchingItem = data.find(ticket => ticket.ticket_type == ticketType);
          return {
            ticket_type: ticketType,
            ticket_type_hebrew: matchingItem ? matchingItem.ticket_type_hebrew : '',
            num_of_tickets_in_ticket_type: data.filter(ticket => ticket.ticket_type == ticketType).length,
            selected: false
          };
        });
        this.displayTicketTypeList = this.ticketTypeList;

        /*get the statuses from the data*/
        const statusesArr = data
          .filter(item => item.status !== null && item.status !== undefined && item.status.toString().trim() !== "")
          .map(item => item.status);
        const uniqueStatuses = [...new Set(statusesArr)];
        this.statusList = uniqueStatuses.map(status => {
          const matchingItem = data.find(ticket => ticket.status == status);
          return {
            status: status,
            status_txt: matchingItem ? matchingItem.status_txt : '',
            num_of_tickets_in_status: data.filter(ticket => ticket.status == status).length,
            selected: false
          };
        });
        this.displayStatusList = this.statusList;

        this.sortAllArrays();
      });
		});

    this.ElForm = this.fb.group({
      neighborhood:new FormControl([]),
      site:new FormControl([]),
      type:new FormControl([]),
      collectionMode:new FormControl([]),
      eventCause:new FormControl([]),
      ticketType:new FormControl([]),
      status:new FormControl([])
    });
  }

  ngOnInit() {
    this.onResize();
    this.responsiveService.checkWidth();
  }

  onResize() {
		this.responsiveService.getMobileStatus().subscribe(isMobile => {
		  this.isMobile = isMobile;
		});
	}

  sortAllArrays() {
    this.displaySitesList.sort((a, b) => {              
      if (a.site < b.site) return -1;
      if (a.site > b.site) return 1;
      return 0;
    });

    this.displayTypesList.sort((a, b) => {              
      if (a.type < b.type) return -1;
      if (a.type > b.type) return 1;
      return 0;
    });

    const clean = (str: string) => str.trim().normalize('NFKD').replace(/[\u200E\u200F]/g, '');
    this.displayNeighborhoodsList.sort((a, b) =>
      clean(a.neighborhood).localeCompare(clean(b.neighborhood), this.currentLang, {
        numeric: true,
        sensitivity: 'accent'
      })
    );

    this.displayCollectionModeList.sort((a, b) => {              
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });

    this.displayEventCauseList.sort((a, b) => {
      const aVal = this.currentLang == 'iw' ? (a.eventCauseHe || '') : (a.eventCauseEn || '');
      const bVal = this.currentLang == 'iw' ? (b.eventCauseHe || '') : (b.eventCauseEn || '');
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });

    this.displayTicketTypeList.sort((a, b) => {
      const aVal = this.currentLang == 'iw' ? (a.ticket_type_hebrew || '') : (a.ticket_type || '');
      const bVal = this.currentLang == 'iw' ? (b.ticket_type_hebrew || '') : (b.ticket_type || '');
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });

    this.displayStatusList.sort((a, b) => {
      if (a.status_txt < b.status_txt) return -1;
      if (a.status_txt > b.status_txt) return 1;
      return 0;
    });
  }

  

  searchSites(value: any) { 
    let filter = value['value'].toLowerCase();
    this.displaySitesList = this.sitesList.filter(site => site.site.toLowerCase().includes(filter));
  }

  searchTypes(value: any) { 
    let filter = value['value'].toLowerCase();
    this.displayTypesList = this.typesList.filter(type => type.type.toLowerCase().includes(filter));
  }

  searchNeighborhoods(value: any) { 
    let filter = value['value'].toLowerCase();
    this.displayNeighborhoodsList = this.neighborhoodsList.filter(neighborhood => neighborhood.neighborhood.toLowerCase().includes(filter));
  }

  searchCollectionModes(value: any) { 
    let filter = value['value'].toLowerCase();
    this.displayCollectionModeList = this.collectionModeList.filter(collectionMode => collectionMode.name.toLowerCase().includes(filter));
  }

  searchEventCauses(value: any) { 
    let filter = value['value'].toLowerCase();
    this.displayEventCauseList = this.eventCauseList.filter(eventCause => {
      const displayValue = this.currentLang == 'iw' ? (eventCause.eventCauseHe || '') : (eventCause.eventCauseEn || '');
      return displayValue.toLowerCase().includes(filter);
    });
  }

  searchTicketTypes(value: any) {
    let filter = value['value'].toLowerCase();
    this.displayTicketTypeList = this.ticketTypeList.filter(ticketType => {
      const displayValue = this.currentLang == 'iw' ? (ticketType.ticket_type_hebrew || '') : (ticketType.ticket_type || '');
      return displayValue.toLowerCase().includes(filter);
    });
  }

  searchStatuses(value: any) {
    let filter = value['value'].toLowerCase();
    this.displayStatusList = this.statusList.filter(status => (status.status_txt || '').toLowerCase().includes(filter));
  }

  clearSiteFilter() {
    this.ElForm.controls['site'].setValue([]);
    this.resetSelection(this.sitesList);
  }

  clearTypeFilter() {
    this.ElForm.controls['type'].setValue([]);
    this.resetSelection(this.typesList);
  }

  clearNeighborhoodFilter() {
    this.ElForm.controls['neighborhood'].setValue([]);
    this.resetSelection(this.neighborhoodsList);
  }

  clearCollectionModeFilter() {
    this.ElForm.controls['collectionMode'].setValue([]);
    this.resetSelection(this.collectionModeList);
  }

  clearEventCauseFilter() {
    this.ElForm.controls['eventCause'].setValue([]);
    this.resetSelection(this.eventCauseList);
  }

  clearTicketTypeFilter() {
    this.ElForm.controls['ticketType'].setValue([]);
    this.resetSelection(this.ticketTypeList);
  }

  clearStatusFilter() {
    this.ElForm.controls['status'].setValue([]);
    this.resetSelection(this.statusList);
  }

  applySiteFilter() {
    this.filterStatus.emit(this.ElForm.getRawValue());
    this.filterAllArrays();
    this.sitesSelect.close();
  }

  applyTypeFilter() {
    this.filterStatus.emit(this.ElForm.getRawValue());
    this.filterAllArrays();
    this.typesSelect.close();
  }

  applyNeighborhoodFilter() {
    this.filterStatus.emit(this.ElForm.getRawValue());
    this.filterAllArrays();
    this.neighborhoodSelect.close();
  }

  applyCollectionModeFilter() {
    this.filterStatus.emit(this.ElForm.getRawValue());
    this.filterAllArrays();
    this.collectionModeSelect.close();
  }

  applyEventCauseFilter() {
    this.filterStatus.emit(this.ElForm.getRawValue());
    this.filterAllArrays();
    this.eventCauseSelect.close();
  }

  applyTicketTypeFilter() {
    this.filterStatus.emit(this.ElForm.getRawValue());
    this.filterAllArrays();
    this.ticketTypeSelect.close();
  }

  applyStatusFilter() {
    this.filterStatus.emit(this.ElForm.getRawValue());
    this.filterAllArrays();
    this.statusSelect.close();
  }

  filterAllArrays() {
    this.arrayToFilter.subscribe(data => {    
      let filteredBins = data.filter(bin => 
        (this.ElForm.getRawValue()['neighborhood'].length > 0 ? this.ElForm.getRawValue()['neighborhood'].includes(bin.Bin_location?.bin_neighbourhood) : true) &&
        (this.ElForm.getRawValue()['site'].length > 0 ? this.ElForm.getRawValue()['site'].includes(bin.site_name) : true) &&
        (this.ElForm.getRawValue()['type'].length > 0 ? this.ElForm.getRawValue()['type'].includes(bin.bin_type_name) : true) &&
        (this.ElForm.getRawValue()['collectionMode'].length > 0 ? this.ElForm.getRawValue()['collectionMode'].includes(bin.Bin_Restrictions?.collection_mode) : true) &&
        (this.ElForm.getRawValue()['eventCause'].length > 0 ? this.ElForm.getRawValue()['eventCause'].includes(bin.eventCauseEn) : true) &&
        (this.ElForm.getRawValue()['ticketType'].length > 0 ? this.ElForm.getRawValue()['ticketType'].includes(bin.ticket_type) : true) &&
        (this.ElForm.getRawValue()['status'].length > 0 ? this.ElForm.getRawValue()['status'].includes(bin.status) : true)
      ); 
      const sitesArr = filteredBins.map(item => item.site_name);
      this.sitesList = [...new Set(sitesArr.filter(item => item && item.trim() !== ""))];
      this.sitesList = this.sitesList.map(site => ({
        site : site,
        num_of_bins_in_site: filteredBins.filter(bin => bin.site_name == site).length,
        selected: this.ElForm.getRawValue()['site'].includes(site)
      }));
      this.displaySitesList = this.sitesList;

      const typesArr = filteredBins.map(item => item.bin_type_name);
      this.typesList = [...new Set(typesArr.filter(item => item && item.trim() !== ""))];
      this.typesList = this.typesList.map(type => ({
        type : type,
        num_of_bins_in_type: filteredBins.filter(bin => bin.bin_type_name == type).length,
        selected: this.ElForm.getRawValue()['type'].includes(type)
      }));
      this.displayTypesList = this.typesList;

      const neighborhoodArr = filteredBins.map(item => item.Bin_location?.bin_neighbourhood);
      this.neighborhoodsList = [...new Set(neighborhoodArr.filter(item => item && item.trim() !== ""))];
      this.neighborhoodsList = this.neighborhoodsList.map(neighborhood => ({
        neighborhood : neighborhood,
        num_of_bins_in_nbrh: filteredBins.filter(bin => bin.Bin_location.bin_neighbourhood == neighborhood).length,
        selected: this.ElForm.getRawValue()['neighborhood'].includes(neighborhood)
      }));
      this.displayNeighborhoodsList = this.neighborhoodsList;

      const collectionModesArr = filteredBins.map(item => item.Bin_Restrictions?.collection_mode);
      this.collectionModeList = [...new Set(collectionModesArr.filter(item => item !== null && item?.toString().trim() !== ""))];
      this.collectionModeList = this.collectionModeList.map(collectionMode => ({
        id: collectionMode,
        name: collectionMode == 2 ? this.translationsObj.TICKET_MANAGEMENT.INCLUDE_IN_WP : this.translationsObj.MANAGE_BIN.NOT_INCLUDE_IN_WORKPLAN,
        num_of_bins_in_collection_mode: filteredBins.filter(bin => bin.Bin_Restrictions?.collection_mode == collectionMode).length,
        selected: this.ElForm.getRawValue()['collectionMode'].includes(collectionMode)
      }));
      this.displayCollectionModeList = this.collectionModeList;

      const eventCausesArr = filteredBins
        .filter(item => item.eventCauseEn && item.eventCauseEn.trim() !== "")
        .map(item => item.eventCauseEn);
      const uniqueEventCausesEn = [...new Set(eventCausesArr)];
      this.eventCauseList = uniqueEventCausesEn.map(eventCauseEn => {
        const matchingItem = filteredBins.find(bin => bin.eventCauseEn == eventCauseEn);
        return {
          eventCauseEn: eventCauseEn,
          eventCauseHe: matchingItem ? matchingItem.eventCauseHe : '',
          num_of_bins_in_event_cause: filteredBins.filter(bin => bin.eventCauseEn == eventCauseEn).length,
          selected: this.ElForm.getRawValue()['eventCause'].includes(eventCauseEn)
        };
      });
      this.displayEventCauseList = this.eventCauseList;

      const ticketTypesArr = filteredBins
        .filter(item => item.ticket_type && item.ticket_type.toString().trim() !== "")
        .map(item => item.ticket_type);
      const uniqueTicketTypes = [...new Set(ticketTypesArr)];
      this.ticketTypeList = uniqueTicketTypes.map(ticketType => {
        const matchingItem = filteredBins.find(ticket => ticket.ticket_type == ticketType);
        return {
          ticket_type: ticketType,
          ticket_type_hebrew: matchingItem ? matchingItem.ticket_type_hebrew : '',
          num_of_tickets_in_ticket_type: filteredBins.filter(ticket => ticket.ticket_type == ticketType).length,
          selected: this.ElForm.getRawValue()['ticketType'].includes(ticketType)
        };
      });
      this.displayTicketTypeList = this.ticketTypeList;

      const statusesArr = filteredBins
        .filter(item => item.status !== null && item.status !== undefined && item.status.toString().trim() !== "")
        .map(item => item.status);
      const uniqueStatuses = [...new Set(statusesArr)];
      this.statusList = uniqueStatuses.map(status => {
        const matchingItem = filteredBins.find(ticket => ticket.status == status);
        return {
          status: status,
          status_txt: matchingItem ? matchingItem.status_txt : '',
          num_of_tickets_in_status: filteredBins.filter(ticket => ticket.status == status).length,
          selected: this.ElForm.getRawValue()['status'].includes(status)
        };
      });
      this.displayStatusList = this.statusList;

      this.sortAllArrays();
    });
  }

  get getSelectedSites() {
    return this.sitesList.filter(item => item.selected);
  }

  get getSelectedTypes() {
    return this.typesList.filter(item => item.selected);
  }

  get getSelectedNeighborhoods() {
    return this.neighborhoodsList.filter(item => item.selected);
  }

  get getSelectedCollectionModes() {
    return this.collectionModeList.filter(item => item.selected);
  }

  get getSelectedEventCauses() {
    return this.eventCauseList.filter(item => item.selected);
  }

  get getSelectedTicketTypes() {
    return this.ticketTypeList.filter(item => item.selected);
  }

  get getSelectedStatuses() {
    return this.statusList.filter(item => item.selected);
  }

  siteOptionClick(site: any) {
    this.sitesList.find(s => s.site == site.site).selected = !site.selected;
  }

  typeOptionClick(type: any) {
    this.typesList.find(t => t.type == type.type).selected = !type.selected;
  }

  neighborhoodOptionClick(neighborhood: any) {
    this.neighborhoodsList.find(n => n.neighborhood == neighborhood.neighborhood).selected = !neighborhood.selected;
  }
  
  collectionModeOptionClick(collectionMode: any) {
    this.collectionModeList.find(c => c.id == collectionMode.id).selected = !collectionMode.selected;
  }

  eventCauseOptionClick(eventCause: any) {
    this.eventCauseList.find(e => e.eventCauseEn == eventCause.eventCauseEn).selected = !eventCause.selected;
  }

  ticketTypeOptionClick(ticketType: any) {
    this.ticketTypeList.find(t => t.ticket_type == ticketType.ticket_type).selected = !ticketType.selected;
  }

  statusOptionClick(status: any) {
    this.statusList.find(s => s.status == status.status).selected = !status.selected;
  }

  sortOptions(arr) {
    arr.sort((a, b) => Number(b.selected) - Number(a.selected));
  }

  resetSelection(arr) {
    arr.forEach(option => option.selected = false);
  }

  get totalActiveFilters(): number {
    return (
      this.sitesList.filter(s => s.selected).length +
      this.typesList.filter(t => t.selected).length +
      this.neighborhoodsList.filter(n => n.selected).length +
      this.collectionModeList.filter(c => c.selected).length +
      this.eventCauseList.filter(e => e.selected).length +
      this.ticketTypeList.filter(t => t.selected).length +
      this.statusList.filter(s => s.selected).length
    );
  }

  openFilterSheet() {
    const sheetRef = this.bottomSheet.open(FilterBarSheetComponent, {
      panelClass: 'filter-bar-sheet-panel',
      data: {
        page: this.page,
        currentLang: this.currentLang,
        translationsObj: this.translationsObj,
        rawData: this.rawDataCache,
        sitesList: this.sitesList,
        typesList: this.typesList,
        neighborhoodsList: this.neighborhoodsList,
        collectionModeList: this.collectionModeList,
        eventCauseList: this.eventCauseList,
        ticketTypeList: this.ticketTypeList,
        statusList: this.statusList
      }
    });

    sheetRef.afterDismissed().subscribe(result => {
      if (!result) return;

      this.ElForm.controls['site'].setValue(result.site);
      this.ElForm.controls['type'].setValue(result.type);
      this.ElForm.controls['neighborhood'].setValue(result.neighborhood);
      this.ElForm.controls['collectionMode'].setValue(result.collectionMode);
      this.ElForm.controls['eventCause'].setValue(result.eventCause);
      this.ElForm.controls['ticketType'].setValue(result.ticketType);
      this.ElForm.controls['status'].setValue(result.status);

      const lists = result.selectedLists;
      this.sitesList = lists.sitesList;
      this.displaySitesList = [...this.sitesList];
      this.typesList = lists.typesList;
      this.displayTypesList = [...this.typesList];
      this.neighborhoodsList = lists.neighborhoodsList;
      this.displayNeighborhoodsList = [...this.neighborhoodsList];
      this.collectionModeList = lists.collectionModeList;
      this.displayCollectionModeList = [...this.collectionModeList];
      this.eventCauseList = lists.eventCauseList;
      this.displayEventCauseList = [...this.eventCauseList];
      this.ticketTypeList = lists.ticketTypeList;
      this.displayTicketTypeList = [...this.ticketTypeList];
      this.statusList = lists.statusList;
      this.displayStatusList = [...this.statusList];

      this.filterStatus.emit(this.ElForm.getRawValue());
      this.filterAllArrays();
    });
  }

  clearAllFilters() {
    this.clearSiteFilter();
    this.clearNeighborhoodFilter();
    this.clearTypeFilter();
    this.clearCollectionModeFilter();
    this.clearEventCauseFilter();
    this.clearTicketTypeFilter();
    this.clearStatusFilter();
    this.filterStatus.emit(this.ElForm.getRawValue());
    this.filterAllArrays();
  }
}
